import * as Sentry from '@sentry/react'
import type { QueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { useEffect } from 'react'
import { ApiError, me } from '@/api/auth'
import { OfflineBanner } from '@/components/offline-banner'
import { useAuthStore } from '@/stores/auth'

export interface RouterContext {
  queryClient: QueryClient
}

const isDev = import.meta.env.DEV

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

function RootLayout() {
  useSessionBootstrap()
  return (
    <Sentry.ErrorBoundary fallback={SentryFallback}>
      <div className="min-h-dvh bg-background text-foreground antialiased">
        <OfflineBanner />
        <Outlet />
        {isDev ? (
          <>
            <TanStackRouterDevtools position="bottom-right" />
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </>
        ) : null}
      </div>
    </Sentry.ErrorBoundary>
  )
}

// SentryFallback — то, что пользователь видит, когда React «упал». Не
// раскрываем stacktrace; даём простую кнопку перезагрузки.
function SentryFallback({ resetError }: { resetError: () => void }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-4 py-12 text-center">
      <h1 className="text-xl font-semibold">Что-то пошло не так</h1>
      <p className="text-sm text-muted-foreground">
        Мы уже получили уведомление об ошибке. Попробуйте обновить страницу.
      </p>
      <button
        type="button"
        onClick={resetError}
        className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Обновить
      </button>
    </main>
  )
}

/**
 * Session bootstrap: однократный GET /me при монтировании рут-лэйаута.
 * Ошибка 401 — ожидаемый "анонимный" случай, переводим store в
 * unauthenticated (не пытаемся ретраить). Для остальных ошибок тоже
 * считаем сессию отсутствующей, чтобы guard увёл на /login.
 */
function useSessionBootstrap() {
  const setUser = useAuthStore((s) => s.setUser)
  const clear = useAuthStore((s) => s.clear)

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: me,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    // Если в store уже есть user (после login) — /me как smoke check всё
    // равно дешёвый, но можно пропустить. Для T-019 держим простую логику.
  })

  useEffect(() => {
    if (query.isSuccess) {
      setUser(query.data)
      return
    }
    if (query.isError) {
      const err = query.error
      if (err instanceof ApiError && err.status !== 401) {
        // Non-401 error — транспортная проблема, но пускать без сессии
        // безопаснее, чем висеть в loading.
      }
      clear()
    }
  }, [query.isSuccess, query.isError, query.data, query.error, setUser, clear])
}
